import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Loader2 } from "lucide-react";
import { Card } from "@shadcn-in-nx/ui/card";
import CreditUsageChart from "../components/CreditUsageChart";
import CreditUsageTable from "../components/CreditUsageTable";

const RANGE_OPTIONS = [
    { label: "7d", days: 7 },
    { label: "14d", days: 14 },
    { label: "30d", days: 30 },
];

export function UsageTab({ t }) {
    const [days, setDays] = useState(7);
    const [page, setPage] = useState(1);
    const [chartData, setChartData] = useState({ chartData: [], labels: [] });
    const [historyData, setHistoryData] = useState({ logs: [], total: 0, totalPages: 1 });
    const [isLoadingChart, setIsLoadingChart] = useState(true);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);

    const { call: getChart } = useBluelagoon({ method: "getCreditUsageChart" });
    const { call: getHistory } = useBluelagoon({ method: "getCreditUsageHistory" });

    const fetchChart = useCallback(async (d) => {
        setIsLoadingChart(true);
        try {
            const res = await getChart({ days: d });
            if (res?.data?.chartData) setChartData(res.data);
        } finally {
            setIsLoadingChart(false);
        }
    }, [getChart]);

    const fetchHistory = useCallback(async (p) => {
        setIsLoadingHistory(true);
        try {
            const res = await getHistory({ page: p, limit: 50 });
            if (res?.data?.logs) setHistoryData(res.data);
        } finally {
            setIsLoadingHistory(false);
        }
    }, [getHistory]);

    useEffect(() => {
        fetchChart(days);
    }, [days, fetchChart]);

    useEffect(() => {
        fetchHistory(page);
    }, [page, fetchHistory]);

    const tableRef = useRef(null);
    const handlePageChange = (p) => {
        setPage(p);
        tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold">{t("billing.usage.title")}</h2>
                <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                    {RANGE_OPTIONS.map((opt) => (
                        <button
                            key={opt.days}
                            onClick={() => setDays(opt.days)}
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                days === opt.days ? "bg-white shadow text-gray-900 font-medium" : "text-gray-500 hover:text-gray-700"
                            }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            <Card className="p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">{t("billing.usage.dailyUsage")}</h3>
                {isLoadingChart ? (
                    <div className="flex items-center justify-center h-48">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <CreditUsageChart chartData={chartData.chartData} labels={chartData.labels} platform={chartData.platform} />
                )}
            </Card>

            <Card ref={tableRef} className="p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">{t("billing.usage.transactionHistory")}</h3>
                {isLoadingHistory ? (
                    <div className="flex items-center justify-center h-32">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                ) : (
                    <CreditUsageTable
                        logs={historyData.logs}
                        page={historyData.page || page}
                        totalPages={historyData.totalPages}
                        onPageChange={handlePageChange}
                        platform={historyData.platform}
                    />
                )}
            </Card>
        </div>
    );
}
